import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Wordmark } from "@/components/AppShell";
import { Button, Field } from "@/components/kit";
import { GOOGLE_ERR, useAuth } from "@/lib/auth";

const HERO = "https://images.unsplash.com/photo-1706808849780-7a04fbac83ef?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

function GoogleIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden>
      <path fill="currentColor" d="M21.35 11.1H12v2.98h5.35c-.23 1.4-1.66 4.1-5.35 4.1-3.22 0-5.85-2.67-5.85-5.96S8.78 6.26 12 6.26c1.83 0 3.06.78 3.76 1.45l2.56-2.47C16.68 3.7 14.55 2.75 12 2.75 6.9 2.75 2.75 6.9 2.75 12S6.9 21.25 12 21.25c5.34 0 8.88-3.75 8.88-9.04 0-.6-.06-1.06-.14-1.51z" />
    </svg>
  );
}

function Hero() {
  return (
    <div className="relative hidden overflow-hidden lg:block" data-testid="auth-hero">
      <img src={HERO} alt="Modern luxury house exterior with pool" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
      <div className="absolute inset-x-12 bottom-14 max-w-lg">
        <Wordmark />
        <p className="mt-4 text-4xl font-medium leading-tight tracking-tight text-foreground">
          Your listing footage, cut into a branded social video.
        </p>
        <p className="mt-4 text-base text-card-foreground">AI voiceover, music, moving photos and your end card — rendered for you.</p>
      </div>
    </div>
  );
}

export default function AuthPage({ mode }) {
  const { signIn, signUp, signInWithGoogle, googleError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const up = mode === "sign-up";
  const canSubmit = email.length > 3 && password.length > 0 && (!up || name.trim().length > 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    if (up && password.length < 8) return setError("Password must be at least 8 characters");
    setBusy("form");
    try {
      if (up) await signUp(name.trim(), email.trim(), password);
      else await signIn(email.trim(), password);
    } catch (err) {
      setError(err.message);
      setBusy(null);
    }
  };
  const google = () => {
    setError(null);
    setBusy("google");
    try {
      signInWithGoogle();
    } catch {
      setError(GOOGLE_ERR);
      setBusy(null);
    }
  };
  const shown = error ?? googleError;

  // Back button from the Google page restores this page from bfcache: un-stick the button and say so.
  useEffect(() => {
    const onShow = (e) => {
      if (!e.persisted || !sessionStorage.getItem("lr_google_pending")) return;
      sessionStorage.removeItem("lr_google_pending");
      setBusy(null);
      setError(GOOGLE_ERR);
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.1fr_1fr]">
      <Hero />
      <div className="flex items-center justify-center px-6 py-12 sm:px-10">
        <form onSubmit={submit} className="fade-up w-full max-w-[420px]" data-testid={up ? "sign-up-form" : "sign-in-form"} noValidate>
          <p className="text-3xl font-semibold tracking-tight text-primary" data-testid="auth-wordmark">ListingReel</p>
          <p className="mt-1 text-base text-card-foreground">Branded listing videos from your phone.</p>
          <h1 className="mt-12 text-[28px] font-medium tracking-tight">{up ? "Create your account" : "Welcome back"}</h1>
          <div className="mt-6 flex flex-col gap-4">
            {up ? <Field id="auth-name" label="Full name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Jordan Lee"
              autoComplete="name" data-testid="auth-name-input" /> : null}
            <Field id="auth-email" label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@agency.com" autoComplete="email" data-testid="auth-email-input" />
            <Field id="auth-password" label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={up ? "At least 8 characters" : "Your password"} autoComplete={up ? "new-password" : "current-password"}
              data-testid="auth-password-input" />
            {shown ? <p role="alert" className="text-sm font-medium text-destructive" data-testid="auth-error-text">{shown}</p> : null}
            <Button type="submit" title={up ? "Create account" : "Sign in"} loading={busy === "form"} disabled={!canSubmit}
              data-testid="auth-submit-button" />
            <div className="flex items-center gap-3 text-[13px] text-muted-foreground"><span className="h-px flex-1 bg-border" />or<span className="h-px flex-1 bg-border" /></div>
            <Button title="Continue with Google" variant="secondary" icon={GoogleIcon} onClick={google} loading={busy === "google"}
              data-testid="auth-google-button" />
          </div>
          <p className="mt-8 text-center text-base text-card-foreground">
            {up ? "Already have an account? " : "New to ListingReel? "}
            <Link to={up ? "/sign-in" : "/sign-up"} replace className="focus-ring rounded font-semibold text-primary hover:underline"
              data-testid="auth-switch-mode-link">{up ? "Sign in" : "Create account"}</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
